import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Loader2 } from "lucide-react";
import Index from "./pages/Index";

const ConnectPage = lazy(() => import("./pages/ConnectPage"));
const ReceiptPage = lazy(() => import("./pages/ReceiptPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const CheckoutSuccessPage = lazy(() => import("./pages/CheckoutSuccessPage"));
const ReportPage = lazy(() => import("./pages/ReportPage"));
const HelpPage = lazy(() => import("./pages/HelpPage"));
const AdminGatePage = lazy(() => import("./pages/AdminGatePage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ProtectedRoute = lazy(() => import("./components/ProtectedRoute"));
const SubscriptionGate = lazy(() => import("./components/SubscriptionGate"));

const LazyTermsPage = lazy(() => import("./pages/LegalPages").then(m => ({ default: m.TermsPage })));
const LazyPrivacyPage = lazy(() => import("./pages/LegalPages").then(m => ({ default: m.PrivacyPage })));
const LazyRefundsPage = lazy(() => import("./pages/LegalPages").then(m => ({ default: m.RefundsPage })));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
  </div>
);

const queryClient = new QueryClient();

const App = () => (
  <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/connect" element={<ConnectPage />} />
              <Route path="/receipt" element={<ReceiptPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/checkout-success" element={<CheckoutSuccessPage />} />
              <Route path="/report" element={<ReportPage />} />
              <Route path="/dashboard" element={<ProtectedRoute><SubscriptionGate><DashboardPage /></SubscriptionGate></ProtectedRoute>} />
              <Route path="/help" element={<HelpPage />} />
              <Route path="/admin-gate" element={<ProtectedRoute><AdminGatePage /></ProtectedRoute>} />
              <Route path="/terms" element={<LazyTermsPage />} />
              <Route path="/privacy" element={<LazyPrivacyPage />} />
              <Route path="/refunds" element={<LazyRefundsPage />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
